import React, { useState } from 'react';
import {
  Modal,
  M,
  H4,
  IconButton,
  Button,
  S,
  Flex,
} from 'design-system-web';
import styles from './AssignCompanyRoleModal.scss';
import { FONT_AWESOME_REGULAR } from 'lane-shared/helpers/constants/icons';
import { CircleListView, Label, MultiselectField } from 'components';
import { useTranslation } from 'react-i18next';
import {
  ApolloQueryResult,
  OperationVariables,
  useQuery,
  useMutation,
} from '@apollo/client';
import { getAvailableCompanyRoles, assignCompanyRole } from 'graphql-queries';
import { CompanyRole } from 'graphql-query-contracts';
import { getAssignCompanyRoleOutcome } from './helpers/getAssignCompanyRoleOutcome';
// import { assignCompanyRole } from 'lane-shared/graphql/mutation';

type Props = {
  isModalOpen: boolean;
  setModalClose: (value: boolean) => void;
  selectedUser: {
    id: string;
    name: string;
    email: string;
    company: { _id: string; name: string }[];
  };
  channelId: string;
  refetch: (
    variables?: Partial<OperationVariables> | undefined
  ) => Promise<ApolloQueryResult<unknown>>;
};

type RoleOption = {
  value: string;
  label: string;
};

export const AssignCompanyRoleModal = ({
  isModalOpen,
  setModalClose,
  selectedUser,
  channelId,
  refetch,
}: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<RoleOption[]>([]);

  const [assignCompanyRoleMutation] = useMutation(assignCompanyRole);

  // Fetch available company roles from GraphQL
  const { data: rolesData, loading: rolesLoading } = useQuery(
    getAvailableCompanyRoles,
    {
      variables: {
        propertyId: channelId,
      },
      skip: !isModalOpen,
      fetchPolicy: 'cache-and-network',
    }
  );

  const onCloseModal = () => {
    setModalClose(false);
    setSelectedRoles([]);
  };

  const handleSelectRoles = (values: RoleOption[]) => {
    setSelectedRoles(values);
  };

  const handleAssignRoles = async () => {
    if (selectedRoles.length === 0) return;

    setLoading(true);

    try {
      const { data } = await assignCompanyRoleMutation({
        variables: {
          userIds: [selectedUser.id],
          roleTemplateIds: selectedRoles.map(role => role.value),
          channelId,
        },
      });
      const outcome = getAssignCompanyRoleOutcome(
        data?.assignCompanyRole,
        selectedUser.id,
        selectedUser.company
      );

      if (outcome.isAssigned) {
        await refetch();
      }

      window.Toast.show(t(outcome.key, { companies: outcome.companies }), 5000);

      if (outcome.isAssigned) {
        onCloseModal();
      }
    } catch (err) {
      await window.Alert.alert({
        title: t('web.admin.channel.users.all.table.assignCompanyRole.error'),
        error: err,
      });
    }

    setLoading(false);
  };

  // Check if user has no tenant companies
  const hasNoTenants = !selectedUser.company?.length;

  // Map roles from GraphQL response to dropdown options
  const availableRoles: CompanyRole[] =
    rolesData?.getAvailableCompanyRoles ?? [];
  const roleOptions: RoleOption[] = availableRoles.map((role: CompanyRole) => ({
    label: role.name,
    value: role.id,
  }));

  const isButtonDisabled =
    loading || rolesLoading || selectedRoles.length === 0 || hasNoTenants;

  return (
    <Modal
      className={styles.AssignCompanyRoleModal}
      isOpen={isModalOpen}
      onClose={onCloseModal}
      isCloseButtonHidden
    >
      <Flex justify="space-between" align="center">
        <H4>{t('web.admin.channel.users.all.table.assignCompanyRole.title')}</H4>
        <IconButton
          type={FONT_AWESOME_REGULAR}
          onClick={onCloseModal}
          icon="times"
          size="small"
        />
      </Flex>

      <CircleListView
        image={null}
        logo={null}
        name={selectedUser.name}
        className={styles.circleListView}
      >
        <M>{`${selectedUser.name} (${selectedUser.email})`}</M>
      </CircleListView>

      {hasNoTenants ? (
        <S className={styles.warningText}>
          {t('web.admin.channel.users.all.table.assignCompanyRole.noTenants')}
        </S>
      ) : (
        <>
          <Label>
            {t('web.admin.channel.users.all.table.assignCompanyRole.selectRole')}
          </Label>
          <MultiselectField
            testId="rolesSelector"
            items={roleOptions}
            value={selectedRoles}
            onChange={handleSelectRoles}
            isFullWidth
            isClearable={false}
          />

          <S className={styles.infoText}>
            {t('web.admin.channel.users.all.table.assignCompanyRole.info')}
          </S>
        </>
      )}

      <div className={styles.divider} />

      <Flex>
        <Button
          variant="primary"
          size="large"
          className={styles.assignButton}
          onClick={handleAssignRoles}
          disabled={isButtonDisabled}
        >
          {selectedRoles.length > 0
            ? `${t('web.admin.channel.users.all.table.assignCompanyRole.assign')} (${selectedRoles.length})`
            : t('web.admin.channel.users.all.table.assignCompanyRole.assign')}
        </Button>
        <Button
          variant="secondary"
          size="large"
          onClick={onCloseModal}
        >
          {t('web.admin.channel.users.all.table.assignCompanyRole.cancel')}
        </Button>
      </Flex>
    </Modal>
  );
};